import {
  Button,
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogDismiss,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  Form,
  FormControl,
  FormField,
  FormItem,
  FormMessage,
  Input,
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@barfacil/barfacil-ui'
import { zodResolver } from '@hookform/resolvers/zod'
import { Pencil } from 'lucide-react'
import { useState } from 'react'
import { useForm } from 'react-hook-form'

import { CreatePriceListResponse, Status } from '@/api/_schemas/management'
import { useUpdatePriceList } from '@/app/panel/management/price-list/_hooks/use-update-price-list'
import {
  FormCreatePriceListWithStatus,
  FormCreatePriceListWithStatusSchema,
} from '@/app/panel/management/price-list/_schemas/create-price-list'

type UpdatePriceListDialogProps = {
  data: CreatePriceListResponse
}

export const UpdatePriceListDialog = ({ data }: UpdatePriceListDialogProps) => {
  const [open, setOpen] = useState(false)

  const { mutation } = useUpdatePriceList()

  const form = useForm<FormCreatePriceListWithStatus>({
    resolver: zodResolver(FormCreatePriceListWithStatusSchema),
    defaultValues: {
      nome: data.nome,
      situacao: data.situacao,
    },
  })

  function handleSubmit<T>(e: React.FormEvent<T>) {
    e.preventDefault()

    return form.handleSubmit(({ nome, situacao }) => {
      mutation.mutate(
        { nome, situacao: situacao as Status, id: data.cod_lista_preco },
        {
          onSuccess: () => setOpen(false),
        },
      )
    })()
  }

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button className="size-4 text-sky-500" variant="ghost" size="icon">
          <Pencil size={16} />
        </Button>
      </DialogTrigger>

      <DialogContent className="w-full max-w-96 gap-8">
        <DialogHeader>
          <DialogTitle className="leading-normal">
            EDITAR LISTA DE PREÇO
          </DialogTitle>
        </DialogHeader>

        <DialogDescription className="hidden" />

        <DialogBody className="line-clamp-1 p-1">
          <Form {...form}>
            <form className="flex flex-col gap-2" onSubmit={handleSubmit}>
              <FormField
                control={form.control}
                name="nome"
                render={({ field }) => (
                  <FormItem>
                    <FormControl>
                      <Input
                        {...field}
                        placeholder="Nome"
                        disabled={mutation.isPending}
                      />
                    </FormControl>

                    <FormMessage className="absolute w-max" />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="situacao"
                render={({ field }) => (
                  <FormItem className="w-full">
                    <Select onValueChange={field.onChange} value={field.value}>
                      <FormControl>
                        <SelectTrigger
                          className="disabled:pointer-events-auto disabled:cursor-not-allowed disabled:bg-white disabled:opacity-50"
                          disabled={mutation.isPending}
                        >
                          <SelectValue placeholder="Situação" />
                        </SelectTrigger>
                      </FormControl>

                      <SelectContent>
                        <SelectGroup>
                          <SelectItem className="flex gap-1" value="Ativo">
                            Ativo
                          </SelectItem>
                          <SelectItem className="flex gap-1" value="Inativo">
                            Inativo
                          </SelectItem>
                        </SelectGroup>
                      </SelectContent>
                    </Select>

                    <FormMessage className="absolute w-max" />
                  </FormItem>
                )}
              />

              <footer className="mt-2 flex flex-col gap-2 sm:ml-auto sm:flex-row">
                <Button
                  className="sm:w-32"
                  type="button"
                  variant="tertiary"
                  onClick={() => setOpen(false)}
                  disabled={mutation.isPending}
                >
                  CANCELAR
                </Button>

                <Button
                  className="sm:w-32"
                  type="submit"
                  disabled={mutation.isPending}
                  isLoading={mutation.isPending}
                >
                  SALVAR
                </Button>
              </footer>
            </form>
          </Form>
        </DialogBody>

        <DialogDismiss className="absolute right-6 top-6" />
      </DialogContent>
    </Dialog>
  )
}
